import { GraphicComponentTypes, IPoint, IViewBox } from "./types/Graphics.t";
import { PinchGesture, WheelGesture } from "@use-gesture/vanilla";
import store from "@/store"
const TWEEN = require("@tweenjs/tween.js");
export interface OnSetActiveGraphicViewboxCallbackFn {
  (point: IViewBox): void;
}

export interface PointerEventsTypes {
  pointerDown: 'mousedown' | 'touchstart' | 'pointerdown'
  pointerMove: 'mousemove' | 'touchmove' | 'pointermove'
  pointerUp: 'mouseup' | 'touchend' | 'pointerup'
  pointerEnter?: 'pointerenter' | 'mouseenter'
  pointerLeave: 'pointerleave' | 'mouseleave'
}


interface AnimationProps {
  fadeIn: number
  fadeOut: number
  hold: number
}

interface RGBColor {
  r: number
  g: number
  b: number
}

interface RGBAColor extends RGBColor {
  a: number
}

const solidHighlightColor: RGBAColor = { r: 0xD1, g: 0, b: 0, a: 1 }
const solidDefaultColor: RGBAColor = { r: 0, g: 0, b: 0, a: 1 }
const nonSolidHighlightColor: RGBAColor = { r: 0xD1, g: 0, b: 0, a: 0.30 }
const nonSolidDefaultColor: RGBAColor = { r: 0, g: 0, b: 0, a: 0 }

// dynamically crate event types based on capability
const hasTouchEvents = 'ontouchstart' in window;
const hasPointerEvents = window.PointerEvent;
const hasTouch = hasTouchEvents
  || window.DocumentTouch && document instanceof window.DocumentTouch
  || navigator.maxTouchPoints; // IE >=11

export default class SVGInteractionHandler {
  animations: any[];
  animationProps: AnimationProps = {
    fadeIn: 50,
    fadeOut: 200,
    hold: 100,
  };
  svgElement: SVGElement;
  isPointerDown: boolean;
  pointerPosition: IPoint;
  pointerOrigin: IPoint;
  viewbox: IViewBox;
  ugcElement: SVGPatternElement | undefined;
  pinchGesture: any;
  wheelGesture: any;
  graphicComponents: GraphicComponentTypes[];
  graphicUGCComponents: GraphicComponentTypes[];
  events: PointerEventsTypes = {
    pointerDown: !hasTouch ? 'mousedown' : hasTouchEvents ? 'touchstart' : 'pointerdown',
    pointerMove: !hasTouch ? 'mousemove' : hasTouchEvents ? 'touchmove' : 'pointermove',
    pointerUp: !hasTouch ? 'mouseup' : hasTouchEvents ? 'touchend' : 'pointerup',
    pointerLeave: hasTouch && hasPointerEvents ? 'pointerleave' : 'mouseleave'
  }
  onSetActiveGraphicViewboxCallback: OnSetActiveGraphicViewboxCallbackFn;

  public static instance: SVGInteractionHandler | null

  constructor(
    svgElement: SVGElement,
    onSetActiveGraphicViewboxCallback: OnSetActiveGraphicViewboxCallbackFn
  ) {
    if (SVGInteractionHandler.instance) SVGInteractionHandler.instance.destroy()
    SVGInteractionHandler.instance = this

    this.svgElement = svgElement;
    this.onSetActiveGraphicViewboxCallback = onSetActiveGraphicViewboxCallback;

    this.graphicComponents = store.getters["gdm/getActiveSubcomponentIDs"]()
    this.graphicUGCComponents = store.getters["gdm/getActiveSubcomponentIDs"](true)
    this.animations = Array(this.graphicComponents?.length)?.fill(null)

    /* Look for and set the UGC element in the SVG, if it exists */
    let fillElement: SVGPatternElement | undefined = undefined
    for (const item in this.graphicUGCComponents) {
      fillElement = svgElement.querySelector(
        `[id^="${this.graphicUGCComponents[item]}_image"]`
      ) as SVGPatternElement;
      if (fillElement) break;
    }
    this.ugcElement = fillElement?.hasAttribute('viewBox') ? fillElement : undefined

    this.isPointerDown = false;
    this.pointerPosition = { x: 0, y: 0 };
    this.pointerOrigin = { x: 0, y: 0 };

    this.viewbox = this.ugcElement ? {
      x: this.ugcElement.viewBox.baseVal.x,
      y: this.ugcElement.viewBox.baseVal.y,
      width: this.ugcElement.viewBox.baseVal.width,
      height: this.ugcElement.viewBox.baseVal.height,
    } : {
      x: 0, y: 0, width: 0, height: 0
    }

    const { activeComponent } = (store.state as any).standardNavigation;
    const graphic = store.getters["gdm/getGraphicByComponent"](activeComponent);
    const originalGraphic = store.getters["graphicsController/getOriginalGraphicViewBox"]();
    if(!originalGraphic?.IPoint?.width && !originalGraphic?.IPoint?.height && graphic && this.ugcElement) {
      const zoomPrecreated = (store.state as any).recipe.initRecipe?.components[activeComponent]?.sub_components?.[graphic.default_component]?.toolkit_options?.zoomValue;
      store.commit("graphicsController/SET_ORIGINAL_GRAPHIC_VIEWBOX", {
        component: activeComponent,
        graphic: graphic,
        graphicViewZoomBox: {zoomValue: zoomPrecreated || "100", IPoint: this.viewbox }
      });
    }
    // bind functions to this
    this.onPointerDownSelect = this.onPointerDownSelect.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);

    for (let i = 0; i < this.graphicComponents?.length; i++) {
      const element = svgElement.querySelector(`#${this.graphicComponents[i]}`) as HTMLElement
      // only add the selection events if this is NOT a UGC filled component
      if (!element) continue
      if (!(this.ugcElement?.id.includes(element.id))) {
        element.setAttribute('editable', '')
        element.addEventListener(this.events.pointerDown, this.onPointerDownSelect)
      } else {
        element.setAttribute('draggable', '')
        element.addEventListener(this.events.pointerDown, this.onPointerDownSelect)
        element.addEventListener(this.events.pointerDown, this.onPointerDown)
      }
    }

    /* exit if there's not a UGC Element present */
    if (!this.ugcElement) return;

    this.setUpPinchGesture();
    this.setUpWheelGesture();
    this.toggleZoom(true);
  }

  /**
   * Create a new WheelGesture object and set its callback to a function that will resize the SVG
   * element
   */
  private setUpWheelGesture() {
    this.wheelGesture = new WheelGesture(this.svgElement, ({ direction, active }) => {
      let scale = (direction[1] > 0) ? 1.01 : 0.99
      this.ugcElement!.viewBox.baseVal.width *= scale;
      this.ugcElement!.viewBox.baseVal.height *= scale;
      if (!active) {
        this.onSetActiveGraphicViewboxCallback(this.getViewBox());
      }
    });
  }

  /**
   * Create a new PinchGesture object and set its callback to a function that will resize the SVG
   * element
   */
  private setUpPinchGesture() {
    this.pinchGesture = new PinchGesture(this.svgElement, ({ delta, active }) => {
      this.ugcElement!.viewBox.baseVal.width *= (1 - delta[0]);
      this.ugcElement!.viewBox.baseVal.height *= (1 - delta[0]);
      if (!active) {
        this.onSetActiveGraphicViewboxCallback(this.getViewBox());
      }
    });
  }

  /**
   * It returns the viewBox of the UGC element.
   * @returns {IViewBox} viewbox
   */
  private getViewBox(): IViewBox {
    return {
      x: this.ugcElement!.viewBox.baseVal.x,
      y: this.ugcElement!.viewBox.baseVal.y,
      width: this.ugcElement!.viewBox.baseVal.width,
      height: this.ugcElement!.viewBox.baseVal.height,
    }
  }

  /**
   * It disables or enables zooming on the page (viewport scaling).
   * @param {boolean} disable - whether to disable or not the viewport scaling
   */
  private toggleZoom(disable: boolean) {
    const meta = document.querySelector('meta[name="viewport"]');
    const allowZoom = "width=device-width, initial-scale=1.0";
    const disallowZoom =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    meta?.setAttribute("content", disable ? disallowZoom : allowZoom);
  }

  private onPointerDownSelect(event: Event): void {
    this.highlightElement(event)
  }

  private onPointerDown(event: Event): void {
    const id = (event.currentTarget as HTMLElement).id

    if (!this.ugcElement) return;
    this.isPointerDown = true;
    this.pointerPosition = this.pointerOrigin = this.getPointFromEvent(event);

    this.svgElement.addEventListener(this.events.pointerUp, this.onPointerUp);
    this.svgElement.addEventListener(this.events.pointerLeave, this.onPointerUp);
    this.svgElement.addEventListener("touchcancel", this.onPointerUp);
    this.svgElement.addEventListener(this.events.pointerMove, this.onPointerMove);
  }

  private onPointerUp(event: Event | MouseEvent | TouchEvent) {
    if (!this.ugcElement) return;
    this.isPointerDown = false;
    this.removeEventListeners(false);
    this.viewbox.x = Math.round(this.ugcElement.viewBox.baseVal.x);
    this.viewbox.y = Math.round(this.ugcElement.viewBox.baseVal.y);
    this.onSetActiveGraphicViewboxCallback(this.getViewBox());
  }

  private onPointerMove(event: MouseEvent | TouchEvent) {
    if (!this.ugcElement) return;
    event.preventDefault();
    // Only run this function if the pointer is down
    if (!this.isPointerDown) return;
    // get the pointer position
    this.pointerPosition = this.getPointFromEvent(event);
    const payload = { x: 0, y: 0 };
    // do some math,  and update the viewbox data
    this.ugcElement.viewBox.baseVal.x = payload.x =
      this.viewbox.x - (this.pointerPosition.x - this.pointerOrigin.x);
    this.ugcElement.viewBox.baseVal.y = payload.y =
      this.viewbox.y - (this.pointerPosition.y - this.pointerOrigin.y);
  }

  private getPointFromEvent(event: Event): IPoint {
    if (event.type.includes('touch')) {
      return {
        x: (<TouchEvent>event).targetTouches[0].clientX,
        y: (<TouchEvent>event).targetTouches[0].clientY,
      };
    }

    return {
      x: (<MouseEvent>event).clientX,
      y: (<MouseEvent>event).clientY,
    };
  }

  private hexToRgb(hex: string): RGBColor | null {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex: RegExp = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const normalizedHex = hex.replace(shorthandRegex, function (m, r, g, b): string {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  private highlightElement = (event: Event): void => {
    event.preventDefault()
    const target: HTMLElement = event.currentTarget as HTMLElement
    const id = target.id as GraphicComponentTypes

    // do not highlight if currently dragging
    if (this.isPointerDown) return

    const elementIndex = this.graphicComponents.indexOf(id as GraphicComponentTypes)

    const targetFill: string = target.getAttribute("fill") as string
    // if the fill includes url, it is a pattern or ugc fill
    const isSolid: boolean = !targetFill.includes("url")
    const rgbTargetFill: RGBColor = isSolid ? this.hexToRgb(targetFill) as RGBColor : solidDefaultColor

    // destroy active tweens
    if (this.animations[elementIndex]) {
      this.animations[elementIndex].stop()
      TWEEN.default.remove(this.animations[elementIndex])
      this.animations[elementIndex] = null
      target.style.fill = ""
    }

    const initialColorValues: RGBAColor = isSolid ? { ...rgbTargetFill, a: 1 } : { ...nonSolidDefaultColor }
    const colorValues: RGBAColor = isSolid ? { ...initialColorValues } : { ...nonSolidDefaultColor }
    const finalColorValues: RGBAColor = isSolid ? { ...solidHighlightColor } : { ...nonSolidHighlightColor }
    const animationTarget: HTMLElement = isSolid ? target : this.createClone(target)

    const highlightAnimation = new TWEEN.default.Tween(colorValues!)
      .to(finalColorValues!, this.animationProps.fadeIn)
      .easing(TWEEN.default.Easing.Linear.None)
      .onUpdate(() => {
        animationTarget.style.fill = `rgba(${colorValues.r}, ${colorValues.g}, ${colorValues.b}, ${colorValues.a})`
      })
    const revertHighlightAnimation = new TWEEN.default.Tween(colorValues!)
      .to(initialColorValues!, this.animationProps.fadeOut)
      .easing(TWEEN.default.Easing.Linear.None)
      .onUpdate(() => {
        animationTarget.style.fill = `rgba(${colorValues.r}, ${colorValues.g}, ${colorValues.b}, ${colorValues.a})`
      })
      .onComplete(() => {
        animationTarget.style.fill = ""
        this.animations[elementIndex] = null
        if (!isSolid) animationTarget.remove()
        this.updateNavigation(id)
      })
      .delay(this.animationProps.hold)
    highlightAnimation.chain(revertHighlightAnimation)
    highlightAnimation.start()
    this.animations[elementIndex] = highlightAnimation
  }

  private createClone(target: HTMLElement): HTMLElement {
    const clone: HTMLElement =
      this.svgElement.querySelectorAll(`#${target.id}_clone`).length
        ? this.svgElement.querySelector(`#${target.id}_clone`) as HTMLElement
        : target.cloneNode(true) as HTMLElement

    // only setup some values if the clone doesnt already exist
    if (!clone.id.includes("_clone")) {
      // we want to remove the fill on our clone
      clone.removeAttribute("fill")
      // set the default fill color (transparent red)
      clone.setAttribute("fill", `rgba(${solidHighlightColor.r}, ${solidHighlightColor.g}, ${solidHighlightColor.b}, 0)`)
      // set a unique id with _clone appended
      clone.id = clone.id + "_clone"
      // we don't want the clone in interact with the mouse/touch
      clone.style.pointerEvents = "none"
      // add the cloned element to the DOM directly after the source node
      this.svgElement.insertBefore(clone, target.nextSibling)
    }
    return clone
  }

  private updateNavigation(id: GraphicComponentTypes) {
    store.dispatch("standardNavigation/UPDATE_STANDARD_NAVIGATION", {
      activeComponent: (store.state as any).standardNavigation.activeComponent,
      activeSubComponent: id,
      menu: (store.state as any).standardNavigation.menu
    });
  }

  private removeEventListeners(removeDownEvent: boolean = true) {
    this.svgElement.removeEventListener(this.events.pointerUp, this.onPointerUp);
    this.svgElement.removeEventListener(this.events.pointerLeave, this.onPointerUp);
    this.svgElement.removeEventListener("touchcancel", this.onPointerUp);
    this.svgElement.removeEventListener(this.events.pointerMove, this.onPointerMove);

    if (!removeDownEvent) return
    for (let i = 0; i < this.graphicComponents?.length; i++) {
      const element = this.svgElement.querySelector(`#${this.graphicComponents[i]}`) as HTMLElement
      if (!element) continue
      if (!(this.ugcElement?.id.includes(element.id))) {
        element.removeEventListener(this.events.pointerDown, this.onPointerDownSelect)
      } else {
        element.removeEventListener(this.events.pointerDown, this.onPointerDownSelect)
        element.removeEventListener(this.events.pointerDown, this.onPointerDown)
      }
    }
  }

  /**
   * Zoom the graphic when it has an image
   * @param scale
   */
  public setZoom(scale: number) {
    const originalGraphic = store.getters["graphicsController/getOriginalGraphicViewBox"]();
    this.ugcElement!.viewBox.baseVal.width = originalGraphic?.IPoint?.width * scale;
    this.ugcElement!.viewBox.baseVal.height = originalGraphic?.IPoint?.height * scale;
    this.onSetActiveGraphicViewboxCallback(this.getViewBox());
  }

  destroy() {
    this.removeEventListeners();
    this.pinchGesture?.destroy();
    this.wheelGesture?.destroy();
    this.toggleZoom(false);
    SVGInteractionHandler.instance = null
  }
}
